import { TIME_ZONE } from './format'

/**
 * Calendar dates (entry dates, expense dates, report ranges) travel as ISO strings `YYYY-MM-DD`,
 * exactly as the API takes them. Arithmetic here runs in UTC on those strings, so no time zone can
 * move a date by a day; only "today" is a real instant, and it is today in Muscat.
 */
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

export interface DateParts {
  year: number
  /** 1–12 */
  month: number
  day: number
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function parseIsoDate(value: string | null | undefined): DateParts | null {
  const match = value ? ISO.exec(value) : null
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null
  return { year, month, day }
}

export function isIsoDate(value: string | null | undefined): boolean {
  return parseIsoDate(value) !== null
}

export function toIsoDate({ year, month, day }: DateParts): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function fromUtc(date: Date): string {
  return toIsoDate({ year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() })
}

function toUtc(iso: string): Date {
  const parts = parseIsoDate(iso)
  if (!parts) throw new RangeError(`Not an ISO date: ${iso}`)
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day))
}

let todayFormat: Intl.DateTimeFormat | undefined

/** Today's date in Muscat (the business day), whatever the browser's time zone. */
export function todayIso(now: Date = new Date()): string {
  todayFormat ??= new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = todayFormat.formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value)
  return toIsoDate({ year: part('year'), month: part('month'), day: part('day') })
}

export function addDays(iso: string, days: number): string {
  const date = toUtc(iso)
  date.setUTCDate(date.getUTCDate() + days)
  return fromUtc(date)
}

/** Same day in another month, clamped to that month's length (31 Jan + 1 month → 28/29 Feb). */
export function addMonths(iso: string, months: number): string {
  const { year, month, day } = parseIsoDate(iso) ?? { year: 1970, month: 1, day: 1 }
  const index = year * 12 + (month - 1) + months
  const nextYear = Math.floor(index / 12)
  const nextMonth = (index % 12) + 1
  return toIsoDate({ year: nextYear, month: nextMonth, day: Math.min(day, daysInMonth(nextYear, nextMonth)) })
}

export function startOfMonth(iso: string): string {
  const parts = parseIsoDate(iso)
  return parts ? toIsoDate({ ...parts, day: 1 }) : iso
}

export function endOfMonth(iso: string): string {
  const parts = parseIsoDate(iso)
  return parts ? toIsoDate({ ...parts, day: daysInMonth(parts.year, parts.month) }) : iso
}

export function startOfYear(iso: string): string {
  const parts = parseIsoDate(iso)
  return parts ? toIsoDate({ year: parts.year, month: 1, day: 1 }) : iso
}

export function endOfYear(iso: string): string {
  const parts = parseIsoDate(iso)
  return parts ? toIsoDate({ year: parts.year, month: 12, day: 31 }) : iso
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(iso: string): number {
  return toUtc(iso).getUTCDay()
}

/** `DD/MM/YYYY`: the editable form of a date (day first, as written in Oman). */
export function toTypedDate(iso: string): string {
  const parts = parseIsoDate(iso)
  if (!parts) return ''
  return `${String(parts.day).padStart(2, '0')}/${String(parts.month).padStart(2, '0')}/${parts.year}`
}

const ARABIC_DIGITS = /[٠-٩۰-۹]/g

/** Arabic-Indic (and Persian) digits typed on an Arabic keyboard become Latin digits. */
export function latinDigits(text: string): string {
  return text.replace(ARABIC_DIGITS, (d) => {
    const code = d.charCodeAt(0)
    return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660)
  })
}

const TODAY_WORDS = new Set(['t', 'today', 'اليوم', 'ت'])

/** Month names as the app displays them (English, and the Arabic of `formatDate`), and their short forms. */
const MONTH_NAMES: readonly (readonly string[])[] = [
  ['jan', 'january', 'يناير', 'كانون الثاني'],
  ['feb', 'february', 'فبراير', 'شباط'],
  ['mar', 'march', 'مارس', 'آذار'],
  ['apr', 'april', 'أبريل', 'ابريل', 'إبريل', 'نيسان'],
  ['may', 'مايو', 'أيار'],
  ['jun', 'june', 'يونيو', 'حزيران'],
  ['jul', 'july', 'يوليو', 'تموز'],
  ['aug', 'august', 'أغسطس', 'اغسطس', 'آب'],
  ['sep', 'sept', 'september', 'سبتمبر'],
  ['oct', 'october', 'أكتوبر', 'اكتوبر', 'تشرين الأول'],
  ['nov', 'november', 'نوفمبر', 'تشرين الثاني'],
  ['dec', 'december', 'ديسمبر', 'كانون الأول'],
]

/** "30 Jun 2026" / "30 يونيو 2026" → "30 6 2026": a month written as a name becomes its number. */
function monthNamesToNumbers(value: string): string {
  let out = value
  MONTH_NAMES.forEach((names, index) => {
    // Longest first, so "june" is not read as "jun" + "e".
    for (const name of [...names].sort((a, b) => b.length - a.length)) {
      out = out.replace(new RegExp(`(^|[\\s/.,-])${name}(?=$|[\\s/.,-])`, 'u'), `$1 ${index + 1} `)
    }
  })
  return out
}

/**
 * Reads a date the way people type it: `25/9/2026`, `25-09-26`, `25.9`, `25` (this month),
 * `2026-09-25`, the way the app shows it (`30 Jun 2026`, `30 يونيو 2026`), or `t` / `today` /
 * `اليوم`. Day comes first. Returns an ISO date, or null.
 */
export function parseTypedDate(text: string, today: string = todayIso()): string | null {
  const value = latinDigits(text).trim().toLowerCase()
  if (!value) return null
  if (TODAY_WORDS.has(value)) return today
  if (isIsoDate(value)) return value
  const current = parseIsoDate(today)
  if (!current) return null
  const pieces = monthNamesToNumbers(value)
    .split(/[/.,\-\s]+/)
    .filter(Boolean)
  if (pieces.length === 0 || pieces.length > 3 || pieces.some((p) => !/^\d+$/.test(p))) return null
  const [dayText, monthText, yearText] = pieces
  const day = Number(dayText)
  const month = monthText === undefined ? current.month : Number(monthText)
  let year = yearText === undefined ? current.year : Number(yearText)
  if (yearText !== undefined && yearText.length <= 2) year += 2000
  if (yearText !== undefined && yearText.length === 3) return null
  const iso = toIsoDate({ year, month, day })
  return isIsoDate(iso) ? iso : null
}
